import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import Loader from "./loader";
import OrderEditModal from "./generateOrders/OrderEditModal";
import OrderSearchResults from "./generateOrders/orderSearchResults";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { useGetOrderPaginationQuery, useUpdateOrderMutation } from "../redux/RTK/orderManagementSlice";

const EMPTY_SEARCH_FORM = {
  companyNumber: "",
  orderNumber: "",
  customerOrderNumber: "",
  appName: "",
  fullName: "",
  state: "",
  orderCreatedOn: "",
};

function normalizeSearchForm(data) {
  return {
    companyNumber: (data.companyNumber ?? "").trim(),
    orderNumber: (data.orderNumber ?? "").trim(),
    customerOrderNumber: (data.customerOrderNumber ?? "").trim(),
    appName: (data.appName ?? "").trim(),
    fullName: (data.fullName ?? "").trim(),
    state: (data.state ?? "").trim(),
    orderCreatedOn: data.orderCreatedOn || "",
  };
}

function OrderAddressManagement() {
  const [startDate, setStartDate] = useState(null);
  const { companyName, clientId, divisionNumber, userId } = useSelector(
    (state) => state.auth,
  );
  const [errors, setErrors] = useState({
    companyNumber: "",
    orderNumber: "",
  });
  const [committedSearchForm, setCommittedSearchForm] = useState(() => ({
    ...EMPTY_SEARCH_FORM,
  }));
  const [updateOrder, { data: updateResults, isLoading: updateLoading, error: updateError }] = useUpdateOrderMutation();
  const [formData, setFormData] = useState({
    ...EMPTY_SEARCH_FORM,
  });
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const itemsPerPage = 10;

  const orderListPayload = useMemo(
    () => ({
      page: currentPage - 1,
      size: itemsPerPage,
      ...committedSearchForm,
    }),
    [currentPage, committedSearchForm],
  );

  const { data: searchResults, isLoading, error, refetch } =
    useGetOrderPaginationQuery(orderListPayload);

  let environment = process.env.REACT_APP_ENV;


  // Initialize orders on component mount
  useEffect(() => {
    if (searchResults?.responseBody) {
      setOrders(searchResults?.responseBody);
    }
  }, [searchResults]);

  useEffect(() => {
    const content = searchResults?.responseBody?.content;
    if (Array.isArray(content) && content.length === 0) {
      toast.error("No orders found");
    }
  }, [searchResults]);

  useEffect(() => {
    if (error) {
      if(error.data.errorMessage === "token is expired") {
        toast.error("Token Expired. Please login again.");
        goHome();
      }else{
        toast.error("Failed to search orders");
      }
    }
  }, [error]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      orderCreatedOn: startDate ? format(startDate, "yyyy-MM-dd") : "",
    }));
  }, [startDate]);

  // Calculate pagination
  const totalPages = orders?.totalPages;
  // const indexOfLastItem = orders?.page * orders?.size;
  // const indexOfFirstItem = indexOfLastItem - orders?.size;
  const currentOrders = orders?.content;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Smart pagination display logic
  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage <= 3) {
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startResult = (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, orders?.content?.length);

  const handleChange = (field, value) => {
    // Only allow numbers for companyNumber
    if (field === "companyNumber") {
      value = value.replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    // let newErrors = {};
    // const orderTrim = formData.orderNumber.trim();
    // const companyTrim = formData.companyNumber.trim();

    // At least one required; if Order Number is filled, only validate that; else validate Company Number
    // if (!orderTrim && !companyTrim) {
    //   newErrors.companyNumber = "Enter Company Number or Order Number";
    //   newErrors.orderNumber = "Enter Company Number or Order Number";
    // } else if (orderTrim) {
    //   if (orderTrim.length <= 4) {
    //     newErrors.orderNumber =
    //       "Please enter a valid Order Number (more than 4 characters)";
    //   }
    // } else if (companyTrim.length !== 4) {
    //   newErrors.companyNumber =
    //     "Please enter a valid 4-digit Company Number";
    // }

    // setErrors(newErrors);

    // if (Object.keys(newErrors).length > 0) {
    //   return;
    // }
    const nextCommitted = normalizeSearchForm(formData);
    const unchanged =
      currentPage === 1 &&
      JSON.stringify(nextCommitted) === JSON.stringify(committedSearchForm);

    setCurrentPage(1);
    if (unchanged) {
      queueMicrotask(() => refetch());
    } else {
      setCommittedSearchForm(nextCommitted);
    }
  };

  const handleClear = () => {
    setStartDate(null);
    setFormData({ ...EMPTY_SEARCH_FORM });
    setCommittedSearchForm({ ...EMPTY_SEARCH_FORM });
    setErrors({
      companyNumber: "",
      orderNumber: "",
    });
    setCurrentPage(1);
  };

  const handleUpdateClick = (order) => {
    setSelectedOrder(order);
  };

  const handleSaveOrder = async (updatedOrder) => {
    const response = await updateOrder({...updatedOrder, updatedBy: userId || 1});
    if (response.error || response.data.error) {
      toast.error("Failed to update order", {
        position: "top-right",
      });
    } else {
      toast.success(`Order ${updatedOrder.orderNumber} updated successfully!`, {
        position: "top-right",
      });
      setSelectedOrder(null);
      refetch();
    }
  };

  //back
  const goBack = (e) => {
    e.preventDefault();
    let redirectUrl =
      environment === "dev" || environment === "qa"
        ? `https://${environment}.hibbertordering.com/home`
        : `https://hibbertordering.com/home`;

    window.location.href = redirectUrl;
  };

  const goHome = () => {
    let redirectUrl =
      environment === "dev" || environment === "qa"
        ? `https://${environment}.hibbertordering.com/home`
        : `https://hibbertordering.com/home`;

    window.location.href = redirectUrl;
  };

  return (
    <div>
      {(isLoading || updateLoading) && <Loader />}
      <ToastContainer />
      <div className="container">
        <div className="group">
          <div className="nav2 span12">
            <div className="user">
              <em style={{ fontSize: "18px" }} className="fa fa-user"></em>{" "}
              {clientId} - {divisionNumber} | {companyName}{" "}
            </div>
            <div className="bread-crumb">
              {" "}
              <Link to="" onClick={goHome}>
                <i style={{ fontSize: "18px" }} className="fa fa-home"></i> Home
              </Link>{" "}
              / Order Address Management{" "}
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="order-search-container">
          <div className="section-card order-search-section">
            <h1 className="section-title">Order Address Update</h1>
            <article className="">
              <form className="search-form">
                <div className="form-section">
                  <div className="group">
                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">
                          Company Number{" "}
                        </label>
                        <input
                          type="text"
                          value={formData.companyNumber}
                          maxLength={4}
                          pattern="[0-9]*"
                          inputMode="numeric"
                          onChange={(e) =>
                            handleChange("companyNumber", e.target.value)
                          }
                          placeholder="Enter company number"
                          className="form-input"
                        />
                        {/* {errors.companyNumber && (
                          <p className="error-message">
                            {errors.companyNumber}
                          </p>
                        )} */}
                      </div>
                    </div>

                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">
                          Order Number{" "}
                        </label>
                        <input
                          type="text"
                          value={formData.orderNumber}
                          onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[a-zA-Z0-9]*$/; // letters and numbers only

                            if (regex.test(value)) {
                              handleChange("orderNumber", value);
                            }
                          }}
                          placeholder="Enter order number"
                          className="form-input"
                        />
                        {/* {errors.orderNumber && (
                          <p className="error-message">{errors.orderNumber}</p>
                        )} */}
                      </div>
                    </div>

                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">
                          Customer Order Number
                        </label>
                        <input
                          type="text"
                          value={formData.customerOrderNumber}
                          onChange={(e) =>
                            handleChange("customerOrderNumber", e.target.value)
                          }
                          placeholder="Enter customer order number"
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">App Name</label>
                        <input
                          type="text"
                          value={formData.appName}
                          onChange={(e) =>
                            handleChange("appName", e.target.value)
                          }
                          placeholder="Enter app name"
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">Full Name</label>
                        <input
                          type="text"
                          value={formData.fullName}
                          onChange={(e) =>
                            handleChange("fullName", e.target.value)
                          }
                          placeholder="Enter full name"
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          value={formData.state}
                          onChange={(e) =>
                            handleChange("state", e.target.value)
                          }
                          placeholder="Enter state"
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="span4">
                      <div className="field-group">
                        <label className="form-label">Order Created On</label>
                        <div className="date-picker">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            isClearable
                            placeholderText="Select date"
                          />{" "}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="button-container">
                  <button
                    className="btn-blue right"
                    style={{ marginLeft: "0px" }}
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                  <button className="btn-red right" onClick={handleClear}>
                    clear
                  </button>
                  <button className="btn-red right" onClick={goBack}>
                    Go Back
                  </button>
                </div>
              </form>
            </article>
          </div>
          <OrderSearchResults
            orders={orders?.content}
            getPageNumbers={getPageNumbers}
            handlePageChange={handlePageChange}
            handleUpdateClick={handleUpdateClick}
            currentPage={currentPage}
            currentOrders={currentOrders}
            totalPages={totalPages}
            startResult={startResult}
            endResult={endResult}
            totalOrders={orders?.totalElements}
          />
        </div>
        {selectedOrder && (
          <OrderEditModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onSave={handleSaveOrder}
            updateLoading={updateLoading}
          />
        )}
      </div>
    </div>
  );
}

export default OrderAddressManagement;
